import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { Home, Users, Calendar, Clock, Plus, Eye, AlertTriangle, CheckCircle, MapPin, UserPlus, RefreshCw } from 'lucide-react';
import { ShiftPeriodBadgeForShift } from '@/components/calendar/ShiftPeriodBadge';
import { SilRatioInlineWarning, RatioRulesScopeNote } from '@/components/calendar/SilRatioSafetyPanel';

function CreateHouseShiftDialog({ house, open, onClose, services, onSuccess }) {
  const [form, setForm] = useState({
    service_id: '',
    shift_date: '',
    start_time: '06:00',
    end_time: '14:00',
    required_staff: house?.recommended_staff || 1,
    support_ratio: '1:3',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  
  // Day 13: Multi-participant support state
  const [supportContext, setSupportContext] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [selectedResidents, setSelectedResidents] = useState([]);

  // Fetch house support context when dialog opens
  useEffect(() => {
    if (open && house?.id) {
      setLoadingContext(true);
      api.get(`/accommodation/houses/${house.id}/support-context`)
        .then(res => {
          const ctx = res.data.data;
          setSupportContext(ctx);
          // Auto-select all residents by default
          if (ctx?.residents) {
            setSelectedResidents(ctx.residents.map(r => r.participant_id));
          }
        })
        .catch(err => {
          console.warn('Failed to fetch support context:', err);
          setSupportContext(null);
        })
        .finally(() => setLoadingContext(false));
    }
  }, [open, house?.id]);

  useEffect(() => {
    if (house) {
      setForm(f => ({
        ...f,
        required_staff: house.recommended_staff || 1,
      }));
    }
  }, [house]);

  const toggleResident = (participantId) => {
    setSelectedResidents(prev => 
      prev.includes(participantId)
        ? prev.filter(id => id !== participantId)
        : [...prev, participantId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.service_id || !form.shift_date) {
      notify.warn('SIL_SHIFT_VALIDATION');
      return;
    }
    setLoading(true);
    try {
      // Build Day 13 multi-participant fields
      const linkedResidents = supportContext?.residents?.filter(r => 
        selectedResidents.includes(r.participant_id)
      ) || [];
      const primaryParticipantId = selectedResidents[0] || null;

      await api.post('/roster/sil/create-shift', {
        house_id: house.id,
        service_id: form.service_id,
        shift_date: form.shift_date,
        start_time: form.start_time,
        end_time: form.end_time,
        required_staff: form.required_staff,
        support_ratio: form.support_ratio,
        notes: form.notes,
        team_id: house.team_id,
        // Day 13: Multi-participant support fields
        support_type: 'sil',
        shift_context_type: 'accommodation',
        linked_house_id: house.id,
        linked_participants: selectedResidents,
        linked_residents: linkedResidents,
        participant_id: primaryParticipantId, // For backward compatibility
      });
      toast.success('SIL shift created with linked residents');
      onSuccess();
      onClose();
    } catch (e) {
      notify.error(e, 'SIL_SHIFT_CREATE_FAILED');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="w-5 h-5 text-green-500" />
            Create SIL House Shift
          </DialogTitle>
          <DialogDescription>
            Create a shift for {house?.name} with multi-resident support
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Day 13: Linked Residents Section */}
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="linked-residents-section">
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-2 text-green-700">
                <Users className="w-4 h-4" />
                Linked Residents
              </Label>
              {loadingContext && (
                <span className="text-xs text-muted-foreground animate-pulse">Loading...</span>
              )}
            </div>
            
            {supportContext?.residents?.length > 0 ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {supportContext.residents.map(r => (
                    <label 
                      key={r.participant_id} 
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                        selectedResidents.includes(r.participant_id) 
                          ? 'bg-green-100 border-green-400' 
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      data-testid={`resident-${r.participant_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedResidents.includes(r.participant_id)}
                        onChange={() => toggleResident(r.participant_id)}
                        className="rounded text-green-600"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{r.participant_name || 'Unknown'}</p>
                        {r.room_name && (
                          <p className="text-xs text-muted-foreground">Room: {r.room_name}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                  <span>{selectedResidents.length} of {supportContext.residents.length} selected</span>
                  <div className="flex gap-2">
                    <button 
                      type="button" 
                      onClick={() => setSelectedResidents(supportContext.residents.map(r => r.participant_id))}
                      className="text-green-600 hover:underline"
                    >
                      Select All
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setSelectedResidents([])}
                      className="text-red-600 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            ) : loadingContext ? (
              <div className="text-sm text-muted-foreground py-2">Loading residents...</div>
            ) : (
              <div className="text-sm text-amber-600 py-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                No active residents in this house
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Service *</Label>
              <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                <SelectTrigger data-testid="house-shift-service">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Shift Date *</Label>
              <Input
                type="date"
                value={form.shift_date}
                onChange={(e) => setForm({ ...form, shift_date: e.target.value })}
                data-testid="house-shift-date"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Start Time</Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              />
            </div>
            <div>
              <Label>End Time</Label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              />
            </div>
            <div>
              <Label>Required Staff</Label>
              <Input
                type="number"
                min={1}
                value={form.required_staff}
                onChange={(e) => setForm({ ...form, required_staff: parseInt(e.target.value) || 1 })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Support Ratio</Label>
              <Select value={form.support_ratio} onValueChange={(v) => setForm({ ...form, support_ratio: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1:1">1:1 (High support)</SelectItem>
                  <SelectItem value="1:2">1:2</SelectItem>
                  <SelectItem value="1:3">1:3 (Standard)</SelectItem>
                  <SelectItem value="1:4">1:4 (Low support)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="text-sm text-muted-foreground">
                Recommended: {house?.recommended_staff} staff based on {selectedResidents.length || house?.current_residents} residents
              </div>
            </div>
          </div>

          {/* Phase A — 1:2 billing-not-ready warning */}
          <SilRatioInlineWarning ratio={form.support_ratio} />

          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Shift notes for staff"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading || loadingContext} data-testid="house-shift-submit">
              {loading ? 'Creating...' : `Create Shift (${selectedResidents.length} residents)`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateRecurringHouseShiftsDialog({ house, open, onClose, services, onSuccess }) {
  const [form, setForm] = useState({
    service_id: '',
    start_date: '',
    end_date: '',
    pattern: 'WEEKLY',
    days_of_week: [0, 1, 2, 3, 4, 5, 6],
    include_morning: true,
    include_afternoon: true,
    include_night: true,
  });
  const [loading, setLoading] = useState(false);

  const DAYS = [
    { value: 0, label: 'Mon' },
    { value: 1, label: 'Tue' },
    { value: 2, label: 'Wed' },
    { value: 3, label: 'Thu' },
    { value: 4, label: 'Fri' },
    { value: 5, label: 'Sat' },
    { value: 6, label: 'Sun' },
  ];

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      days_of_week: f.days_of_week.includes(day)
        ? f.days_of_week.filter(d => d !== day)
        : [...f.days_of_week, day].sort()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.service_id || !form.start_date) {
      notify.warn('SIL_SHIFT_VALIDATION');
      return;
    }

    const shifts_config = [];
    if (form.include_morning) shifts_config.push({ start_time: '06:00', end_time: '14:00', label: 'Morning' });
    if (form.include_afternoon) shifts_config.push({ start_time: '14:00', end_time: '22:00', label: 'Afternoon' });
    if (form.include_night) shifts_config.push({ start_time: '22:00', end_time: '06:00', label: 'Night' });

    if (shifts_config.length === 0) {
      notify.warn('SIL_SHIFT_TIME_REQUIRED');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/roster/sil/create-recurring', {
        house_id: house.id,
        service_id: form.service_id,
        pattern: form.pattern,
        days_of_week: form.days_of_week,
        shifts_config,
        start_date: form.start_date,
        end_date: form.end_date || null,
      });
      toast.success(`Created ${res.data.count || 0} recurring series`);
      onSuccess();
      onClose();
    } catch (e) {
      notify.error(e, 'SIL_RECURRING_FAILED');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-500" />
            Setup Recurring Shifts
          </DialogTitle>
          <DialogDescription>
            Create recurring shift patterns for {house?.name}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Service *</Label>
            <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
              <SelectTrigger data-testid="recurring-house-service">
                <SelectValue placeholder="Select service" />
              </SelectTrigger>
              <SelectContent>
                {services.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Days of Week</Label>
            <div className="flex gap-2 flex-wrap">
              {DAYS.map(d => (
                <Button
                  key={d.value}
                  type="button"
                  variant={form.days_of_week.includes(d.value) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleDay(d.value)}
                >
                  {d.label}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Shift Times</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.include_morning}
                  onChange={(e) => setForm({ ...form, include_morning: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Morning (6am-2pm)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.include_afternoon}
                  onChange={(e) => setForm({ ...form, include_afternoon: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Afternoon (2pm-10pm)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.include_night}
                  onChange={(e) => setForm({ ...form, include_night: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Night (10pm-6am)</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="recurring-house-submit">
              {loading ? 'Creating...' : 'Create Recurring Shifts'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HouseDetailDialog({ house, open, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && house) {
      setLoading(true);
      api.get(`/roster/sil/houses/${house.id}`)
        .then(res => setDetails(res.data.data))
        .catch(() => notify.error(e, 'SIL_HOUSE_LOAD_FAILED'))
        .finally(() => setLoading(false));
    }
  }, [open, house]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="w-5 h-5 text-green-500" />
            {house?.name} - Roster Details
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        ) : details ? (
          <div className="space-y-6">
            {/* Residents */}
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">
                Residents ({details.resident_count})
              </h3>
              {details.residents.length === 0 ? (
                <p className="text-sm text-muted-foreground">No active residents</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {details.residents.map(r => (
                    <div key={r.participant_id} className="text-sm p-2 bg-muted/50 rounded">
                      <p className="font-medium">{r.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.ndis_number} | {r.placement_type}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Team */}
            {details.team && (
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">
                  Assigned Team: {details.team.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {details.team.members.map(m => (
                    <Badge key={m.staff_id} variant="outline">{m.name}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Recommended Staffing */}
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">Recommended Staffing</h3>
              <div className="grid grid-cols-5 gap-2 text-sm">
                {Object.entries(details.recommended_staffing || {}).map(([time, count]) => (
                  <div key={time} className="text-center p-2 bg-muted/50 rounded">
                    <p className="capitalize font-medium">{time}</p>
                    <p className="text-lg font-bold text-blue-600">{count}</p>
                    <p className="text-xs text-muted-foreground">staff</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Shifts */}
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">
                Upcoming Shifts ({details.shifts?.length || 0})
              </h3>
              {details.shifts?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No upcoming shifts scheduled</p>
              ) : (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {details.shifts?.slice(0, 10).map(s => (
                    <div key={s.id} className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                      <div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-medium">{s.date} {s.start_time}-{s.end_time}</p>
                          <ShiftPeriodBadgeForShift shift={s} />
                        </div>
                        <p className="text-xs text-muted-foreground">{s.title}</p>
                      </div>
                      <Badge variant={
                        s.status === 'ASSIGNED' ? 'default' :
                        s.status === 'OPEN' ? 'destructive' :
                        s.status === 'PARTIALLY_FILLED' ? 'secondary' : 'outline'
                      }>
                        {s.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function SILRosterPage() {
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [createShiftDialog, setCreateShiftDialog] = useState(null);
  const [createRecurringDialog, setCreateRecurringDialog] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [housesRes, servicesRes] = await Promise.all([
        api.get('/roster/sil/houses'),
        api.get('/services/catalog'),
      ]);
      setHouses(housesRes.data.data || []);
      setServices(servicesRes.data.data || []);
    } catch (e) {
      notify.error(e, 'SIL_ROSTER_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getTotalUnfilled = () => houses.reduce((sum, h) => sum + (h.unfilled_today || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sil-roster-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Home className="w-6 h-6 text-green-500" />
            SIL House Rostering
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage rosters for Supported Independent Living houses
          </p>
        </div>
        {getTotalUnfilled() > 0 && (
          <Badge variant="destructive" className="text-sm px-3 py-1">
            {getTotalUnfilled()} Unfilled Today
          </Badge>
        )}
      </div>

      {/* Phase A — Ratio Rules scope clarification */}
      <RatioRulesScopeNote />

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Houses</p>
                <p className="text-2xl font-bold">{houses.length}</p>
              </div>
              <Home className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Residents</p>
                <p className="text-2xl font-bold">{houses.reduce((s, h) => s + h.current_residents, 0)}</p>
              </div>
              <Users className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Shifts Today</p>
                <p className="text-2xl font-bold">{houses.reduce((s, h) => s + h.shifts_today, 0)}</p>
              </div>
              <Calendar className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card className={getTotalUnfilled() > 0 ? 'border-red-200 bg-red-50/50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unfilled Today</p>
                <p className={`text-2xl font-bold ${getTotalUnfilled() > 0 ? 'text-red-600' : ''}`}>
                  {getTotalUnfilled()}
                </p>
              </div>
              <AlertTriangle className={`w-8 h-8 ${getTotalUnfilled() > 0 ? 'text-red-400' : 'text-muted-foreground/50'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading houses...</div>
      ) : houses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Home className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No SIL houses found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create houses in the Accommodation module first
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {houses.map(h => (
            <Card key={h.id} className="hover:shadow-md transition-shadow" data-testid={`house-card-${h.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg">{h.name}</h3>
                      {h.team_name && (
                        <Badge variant="outline" className="text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          {h.team_name}
                        </Badge>
                      )}
                      {h.unfilled_today > 0 && (
                        <Badge variant="destructive">{h.unfilled_today} unfilled</Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {h.address}
                      </span>
                    </div>

                    <div className="grid grid-cols-4 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Occupancy</p>
                        <div className="flex items-center gap-2">
                          <Progress value={h.occupancy_rate} className="h-2 flex-1" />
                          <span className="text-sm font-medium">{h.current_residents}/{h.capacity}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Shifts Today</p>
                        <p className="text-lg font-semibold">{h.shifts_today}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Recommended Staff</p>
                        <p className="text-lg font-semibold text-blue-600">{h.recommended_staff}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Status</p>
                        {h.unfilled_today === 0 ? (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle className="w-4 h-4" /> Covered
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            <AlertTriangle className="w-4 h-4" /> Needs Staff
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button size="sm" onClick={() => setCreateShiftDialog(h)} data-testid={`create-shift-${h.id}`}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Shift
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setCreateRecurringDialog(h)}>
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Setup Recurring
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDetailsDialog(h)}>
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {createShiftDialog && (
        <CreateHouseShiftDialog
          house={createShiftDialog}
          open={!!createShiftDialog}
          onClose={() => setCreateShiftDialog(null)}
          services={services}
          onSuccess={fetchData}
        />
      )}

      {createRecurringDialog && (
        <CreateRecurringHouseShiftsDialog
          house={createRecurringDialog}
          open={!!createRecurringDialog}
          onClose={() => setCreateRecurringDialog(null)}
          services={services}
          onSuccess={fetchData}
        />
      )}

      {detailsDialog && (
        <HouseDetailDialog
          house={detailsDialog}
          open={!!detailsDialog}
          onClose={() => setDetailsDialog(null)}
        />
      )}
    </div>
  );
}
